import type {
	ISettingsAdapter,
	ResolutionString,
	SeriesType,
} from "@public/charting_library";

const CHART_PREFERENCES_KEY = "trade-chart:preferences:v1";
const CHART_SETTINGS_KEY = "trade-chart:settings:v1";

type ChartPreferences = {
	interval: ResolutionString;
	chartType: SeriesType;
};

type ChartSettingsMap = Record<string, string>;

function canUseStorage() {
	return typeof window !== "undefined";
}

function readStoredJson<T>(key: string): T | null {
	if (!canUseStorage()) return null;

	try {
		const stored = window.localStorage.getItem(key);
		if (!stored) return null;
		return JSON.parse(stored) as T;
	} catch (error) {
		console.warn(`[Chart] Failed to read localStorage key "${key}".`, error);
		return null;
	}
}

function writeStoredJson(key: string, value: unknown) {
	if (!canUseStorage()) return;

	try {
		window.localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		console.warn(`[Chart] Failed to write localStorage key "${key}".`, error);
	}
}

function removeStoredValue(key: string) {
	if (!canUseStorage()) return;

	try {
		window.localStorage.removeItem(key);
	} catch (error) {
		console.warn(`[Chart] Failed to remove localStorage key "${key}".`, error);
	}
}

export function loadChartPreferences(): Partial<ChartPreferences> | null {
	return readStoredJson<Partial<ChartPreferences>>(CHART_PREFERENCES_KEY);
}

export function saveChartPreference<K extends keyof ChartPreferences>(
	key: K,
	value: ChartPreferences[K],
) {
	const current = loadChartPreferences() ?? {};
	writeStoredJson(CHART_PREFERENCES_KEY, {
		...current,
		[key]: value,
	});
}

export function createChartSettingsAdapter(): ISettingsAdapter {
	const initialSettings =
		readStoredJson<ChartSettingsMap>(CHART_SETTINGS_KEY) ?? {};

	return {
		initialSettings,
		setValue(key, value) {
			const current =
				readStoredJson<ChartSettingsMap>(CHART_SETTINGS_KEY) ?? {};
			writeStoredJson(CHART_SETTINGS_KEY, {
				...current,
				[key]: value,
			});
		},
		removeValue(key) {
			const current =
				readStoredJson<ChartSettingsMap>(CHART_SETTINGS_KEY) ?? {};

			if (!(key in current)) {
				return;
			}

			delete current[key];

			if (Object.keys(current).length === 0) {
				removeStoredValue(CHART_SETTINGS_KEY);
				return;
			}

			writeStoredJson(CHART_SETTINGS_KEY, current);
		},
	};
}
